import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildReportView, INITIAL_STATE } from './view';
import { TopBar } from './sections/TopBar';
import { StatTiles } from './sections/StatTiles';
import { Notices } from './sections/Notices';
import { StatusCards } from './sections/StatusCards';
import { Waiting } from './sections/Waiting';
import { Outcome } from './sections/Outcome';
import { OriginalDocument } from './sections/OriginalDocument';
import { EmptyReport } from './sections/EmptyReport';
import { AiOverview } from './sections/AiOverview';
import { HistoryOverview } from './sections/history/HistoryOverview';
import { MileageChart } from './sections/MileageChart';
import { DocInfo } from './sections/DocInfo';
import { Inspection } from './sections/inspection/Inspection';
import { Hero } from './sections/columns/Hero';
import { PhotoCarousel } from './sections/columns/PhotoCarousel';
import { PriceCard } from './sections/columns/PriceCard';
import { ConditionSummary } from './sections/columns/ConditionSummary';
import { SpecsCards } from './sections/columns/SpecsCards';
import { EquipmentChips } from './sections/columns/EquipmentChips';
import { DocumentPhotos } from './sections/columns/DocumentPhotos';
import './reportPage.scss';

/**
 * The one report page every report type renders (Standard, Internal, Complete). It takes a report view model
 * (built by the adapters from the API data) and shows only the sections that model has data for, laid out like the
 * earlier Complete page: header across the page, then the main column (photos, key numbers, notices, the dealer
 * part's state, AI overview, specs, mileage, inspection, history, documents) beside a sticky side column (price,
 * condition or service summary, status, equipment). On a phone and in print the side column comes after the photos.
 */
const ReportPage = ({ report, bare = false, onOpenImage }) => {
    const { t } = useTranslation(['reportPage']);
    const [state, setState] = useState(INITIAL_STATE);
    const view = useMemo(() => buildReportView(report, state, t, { columns: true }), [report, state, t]);

    const update = changes => setState(current => ({ ...current, ...changes }));
    const actions = {
        toggleZone: zone => setState(current => ({ ...current, zone: current.zone === zone ? null : zone })),
        selectYear: year =>
            setState(current => ({ ...current, year: year == null || current.year === year ? null : year })),
        clearFilters: () => update({ zone: null, year: null }),
        toggleVisit: (key, open) => setState(current => ({ ...current, open: { ...current.open, [key]: open } })),
        toggleAllVisits: () => setState(current => ({ ...current, allVisits: !current.allVisits })),
        toggleAllRepairs: () => setState(current => ({ ...current, allRepairs: !current.allRepairs })),
        toggleAllCodes: () => setState(current => ({ ...current, allCodes: !current.allCodes })),
    };

    const footer = !bare && view.vehicle && (
        <footer className="footer">
            <span>{t('page.footer', { vin: view.vehicle.vin, date: view.reportDate })}</span>
            {view.ai && view.ai.ok && <span>{t('page.aiNote')}</span>}
        </footer>
    );

    const { hero, specs, condition, summary, price, chips } = view.columns;
    const inspection = view.inspection;
    const hasSide = price || condition || summary || view.status.length > 0 || chips;

    return (
        <div className="cvr">
            <div className={`page${bare ? ' bare' : ''}`}>
                {!bare && <TopBar reportDate={view.reportDate} source={view.source} />}
                {hero && <Hero hero={hero} />}
                <div className={`cols${hasSide ? '' : ' single'}`}>
                    {inspection && inspection.photos.length > 0 && (
                        <div className="col-top">
                            <PhotoCarousel photos={inspection.photos} onOpenImage={onOpenImage} />
                        </div>
                    )}
                    {hasSide && (
                        <aside className="col-side">
                            {price && <PriceCard price={price} />}
                            {condition && <ConditionSummary rows={condition} />}
                            {summary && <ConditionSummary rows={summary} title={t('layout.serviceSummary')} />}
                            {view.status.length > 0 && <StatusCards cards={view.status} stacked />}
                            {chips && <EquipmentChips equipment={chips} onToggleAll={actions.toggleAllCodes} />}
                        </aside>
                    )}
                    <div className="col-main">
                        {view.tiles.length > 0 && <StatTiles tiles={view.tiles} />}
                        {view.notices.length > 0 && <Notices notices={view.notices} />}
                        {view.waiting && <Waiting waiting={view.waiting} />}
                        {view.outcome && <Outcome outcome={view.outcome} />}
                        {view.original && <OriginalDocument original={view.original} />}
                        {view.empty && <EmptyReport empty={view.empty} />}
                        {view.ai && <AiOverview ai={view.ai} />}
                        {specs.length > 0 && <SpecsCards cards={specs} />}
                        {view.mileage && <MileageChart chart={view.mileage} />}
                        {inspection && (
                            <Inspection
                                inspection={inspection}
                                onOpenImage={onOpenImage}
                                parts={['paint', 'checks', 'damages']}
                            />
                        )}
                        {view.history.show && (
                            <HistoryOverview history={view.history} state={state} actions={actions} />
                        )}
                        {inspection && inspection.documentPhotos.length > 0 && (
                            <DocumentPhotos photos={inspection.documentPhotos} onOpenImage={onOpenImage} />
                        )}
                        {inspection && (
                            <Inspection
                                inspection={inspection}
                                onOpenImage={onOpenImage}
                                parts={['documents', 'video']}
                            />
                        )}
                        {view.docinfo.fields.length + view.docinfo.files.length > 0 && (
                            <DocInfo docinfo={view.docinfo} />
                        )}
                    </div>
                </div>
                {footer}
            </div>
        </div>
    );
};

export default ReportPage;
